import React, { useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import {SkillsChart, ISkillsChartProps } from "./skills-chart-component";

interface ISkillsChartSliderProps {
  slides: ISkillsChartProps[];
  className?: string;
}

const swipeConfidenceThreshold = 80;

const swipePower = (offset: number, velocity: number) => {
  return Math.abs(offset) * velocity;
};

const variants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 80 : -80,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction < 0 ? 80 : -80,
    opacity: 0,
  }),
};

const Slider: React.FC<ISkillsChartSliderProps> = ({
  slides,
  className = "",
}) => {
  const [[page, direction], setPage] = useState([0, 0]);

  if (!slides.length) return null;

  const paginate = (newDirection: number) => {
    setPage(([prevPage]) => {
      const nextPage = (prevPage + newDirection + slides.length) % slides.length;
      return [nextPage, newDirection];
    });
  };

  return (
    <div className={`relative flex w-full flex-col gap-3 ${className}`}>
      <div className="relative overflow-hidden">
        <AnimatePresence custom={direction} initial={false} mode="wait">
          <motion.div
            key={page}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: "spring", stiffness: 300, damping: 30 },
              opacity: { duration: 0.18 },
            }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.8}
            onDragEnd={(_, { offset, velocity }) => {
              const swipe = swipePower(offset.x, velocity.x);

              if (swipe < -swipeConfidenceThreshold) {
                paginate(1);
              } else if (swipe > swipeConfidenceThreshold) {
                paginate(-1);
              }
            }}
            className="w-full"
          >
            <SkillsChart {...slides[page]} />
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => paginate(-1)}
          className="rounded-[10px] border border-slate-300 px-3 py-1 text-sm transition hover:bg-slate-100"
        >
          ←
        </button>

        <div className="flex items-center gap-2">
          {slides.map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => setPage([index, index > page ? 1 : -1])}
              className={`h-2.5 w-2.5 rounded-full transition ${
                index === page ? "bg-slate-800" : "bg-slate-300 hover:bg-slate-400"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        <button
          type="button"
          onClick={() => paginate(1)}
          className="rounded-[10px] border border-slate-300 px-3 py-1 text-sm transition hover:bg-slate-100"
        >
          →
        </button>
      </div>
    </div>
  );
};

export default Slider;